import {
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Text,
	TextAlignment,
	ThemeProvider,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { Close } from '@volkswagen-onehub/icons-core';
import { LayerHandleV2, ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import { default as React } from 'react';
import { PortalContext } from '../../../context/PortalContext';
import { useTrackingManager } from '../../../tracking/useTrackingManager';
import { ErrorType } from '@esportal/portal-service';
import { formatOwnership } from '../../../tracking/trackingHelper';

export const AlertVehicleManagerLayer = ({
	setIsAlertLayerVisible,
	isAlertLayerVisible,
	setIsJourneyLayerVisible,
	alertLayerRef,
	errorType,
}: {
	setIsAlertLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	isAlertLayerVisible: boolean;
	setIsJourneyLayerVisible?: React.Dispatch<React.SetStateAction<boolean>>;
	alertLayerRef: React.RefObject<LayerHandleV2<any>>;
	errorType: ErrorType;
}): React.ReactElement => {
	const { layerManager, portalService } = React.useContext(PortalContext);
	const errorCountRef = React.useRef<number>(0);

	const trackingManager = useTrackingManager();

	const userVehicles = portalService.getPersonVehicles().getValue();

	React.useEffect(() => {
		if (isAlertLayerVisible) {
			trackingManager.trackClubVWErrorMessageLoad(
				{ message: '¡Ops, ha habido un error!' },
				{
					contentId: `Error: ${errorType}`,
				},
			);
		}
	}, [isAlertLayerVisible]);

	const handleClose = (isCta: boolean) => {
		setIsAlertLayerVisible(false);
		isCta
			? trackingManager.trackClubVWButtonClick(
					'Volver',
					{
						contentId: `Close Error layer: ${errorType}`,
					},
					{ vehicle: formatOwnership(userVehicles?.vehicles) },
				)
			: trackingManager.trackClubVWLayerCloseClick('Error layer', {
					contentId: `Error layer: ${errorType}`,
				});

		if (setIsJourneyLayerVisible) {
			setIsJourneyLayerVisible(true);
		}

		if (alertLayerRef?.current) {
			alertLayerRef?.current.close();
			return ((alertLayerRef.current as any) = null);
		}
		return null;
	};

	const renderAlertLayer = () => {
		return (
			<ThemeProvider theme={'inverted'}>
				<Container
					wrap={'always'}
					padding={ContainerPadding.dynamic0150}
					gutter={ContainerGutter.dynamic0050}
					horizontalAlign={'center'}
				>
					<Container horizontalAlign={'flex-end'}>
						<Text color={TokenTextColor.primary} textAlign={TextAlignment.end} bold>
							<CTA
								tag="button"
								emphasis="tertiary"
								onClick={() => {
									handleClose(false);
								}}
								aria-label="closePopUp"
								icon={<Close variant="default" aria-hidden />}
							/>
						</Text>
					</Container>
					<Container
						wrap={'always'}
						gutter={ContainerGutter.dynamic0050}
						horizontalAlign={'center'}
						verticalAlign={'center'}
					>
						<Text appearance={TokenTextAppearance.headline400} textAlign={TextAlignment.center} bold>
							{errorCountRef.current > 1 ? 'Uy, ha habido un error' : '¡Ops, ha habido un error!'}
						</Text>
						{errorCountRef.current > 1 ? (
							<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
								Si no se resuelve pronto, podemos echarte una mano en el{' '}
								<CTA tag="a" emphasis="tertiary" href="tel:+34800500100">
									800 500 100
								</CTA>
								.
							</Text>
						) : (
							<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
								Vuelve a intentarlo de nuevo
							</Text>
						)}
					</Container>
					<Container padding={{ top: ContainerPadding.dynamic0100 }} horizontalAlign={'center'}>
						<Text color={TokenTextColor.primary} textAlign={TextAlignment.center} bold>
							<CTA
								tag="button"
								emphasis="primary"
								onClick={() => {
									handleClose(true);
								}}
								aria-label="closePopUp"
							>
								<Text bold>Volver</Text>
							</CTA>
						</Text>
					</Container>
				</Container>
			</ThemeProvider>
		);
	};

	const renderInteractionLayer = (): any => {
		if (!alertLayerRef.current && isAlertLayerVisible) {
			errorCountRef.current++;
			const layer = layerManager.openAlert(
				renderAlertLayer,
				{},
				{
					shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
				},
			);
			(alertLayerRef.current as any) = layer;
		}
		return null;
	};

	return <>{isAlertLayerVisible && renderInteractionLayer()}</>;
};
